import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';

/**
 * Backend NATIF de la retranscription (iOS/Android) — npm `@capgo/
 * capacitor-speech-recognition`, enveloppant `SFSpeechRecognizer` (iOS) et
 * `SpeechRecognizer` (Android), enregistré nativement sous le nom
 * `'SpeechRecognition'` (identique à l'ancien `@capacitor-community/
 * speech-recognition` — même identité de plugin, ce paquet en est le
 * successeur activement maintenu ET compatible SPM). Indispensable car le
 * Web Speech API du navigateur (`use-live-transcription.ts`, chemin web) est
 * EXPOSÉ mais NON FONCTIONNEL dans une WKWebView iOS (confirmé :
 * `webkitSpeechRecognition` existe sur `window` mais ne déclenche jamais
 * aucun event — Apple ne l'active que dans Safari lui-même, jamais dans une
 * WKWebView tierce comme celle de Capacitor). Android WebView expose aussi le
 * constructeur mais avec des callbacks qui ne se déclenchent pas de façon
 * fiable.
 *
 * ⚠️ `@capacitor-community/speech-recognition` (le paquet historique) N'A PAS
 * de `Package.swift` — ce projet iOS utilise SPM EXCLUSIVEMENT (pas de
 * Podfile), donc ce paquet-là ne s'installe pas côté iOS (`cap sync` le
 * signale en warning, silencieusement absent du `Package.swift` généré, zéro
 * implémentation native au runtime). `@capgo/capacitor-speech-recognition`
 * fournit À LA FOIS `Package.swift` (SPM) et un `.podspec` (CocoaPods) —
 * compatible avec ce projet sans changement d'outillage iOS.
 *
 * Types MINIMAUX (seules les méthodes réellement utilisées — même convention
 * que `speech-recognition-types.ts` pour le Web Speech API ; le paquet expose
 * une API bien plus riche — `useOnDeviceRecognition`, `contextualStrings`,
 * niveaux audio, PTT continu… — qu'on n'utilise pas), donc le paquet npm
 * n'est dépendance QUE d'`apps/app` (nécessaire pour que `cap sync` installe
 * le SDK natif iOS/Android) — jamais de ce package partagé, qui n'a besoin
 * que de `@capacitor/core` pour `registerPlugin`.
 *
 * ⚠️ Enregistré via `registerPlugin` (proxy SYNCHRONE, zéro chunk) — JAMAIS
 * `import('@capgo/capacitor-speech-recognition')` dynamique, qui resterait
 * bloqué dans la WKWebView iOS (même piège documenté pour
 * `@capacitor-firebase/messaging`, cf. `apps/app/src/lib/notifications/
 * firebase-messaging.ts`).
 */

export type NativeSpeechPermission = 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied';

export interface NativeSpeechPermissionStatus {
  speechRecognition: NativeSpeechPermission;
}

export interface NativeSpeechUtteranceOptions {
  language?: string;
  maxResults?: number;
  prompt?: string;
  /** Popup système de dictée (Android only) — toujours `false` ici : on veut
   * une UI intégrée à la salle vidéo, pas la popup native par-dessus. */
  popup?: boolean;
  /** Toujours `true` ici — sans ça `start()` ne renvoie qu'un résultat unique
   * au lieu d'un flux d'événements `partialResults` jusqu'à `stop()`. */
  partialResults?: boolean;
}

export interface SpeechRecognitionPlugin {
  available(): Promise<{ available: boolean }>;
  start(options?: NativeSpeechUtteranceOptions): Promise<{ matches?: string[] }>;
  stop(): Promise<void>;
  isListening(): Promise<{ listening: boolean }>;
  checkPermissions(): Promise<NativeSpeechPermissionStatus>;
  requestPermissions(): Promise<NativeSpeechPermissionStatus>;
  /** Émis en continu pendant l'écoute — dernière valeur = meilleure transcription courante de l'énoncé en cours. */
  addListener(
    eventName: 'partialResults',
    listenerFunc: (data: { matches: string[] }) => void,
  ): Promise<PluginListenerHandle>;
  /** `stopped` = fin d'énoncé détectée (silence) OU `stop()` appelé — signal qu'il faut poster le dernier `partialResults` puis relancer. */
  addListener(
    eventName: 'listeningState',
    listenerFunc: (data: { status: 'started' | 'stopped' }) => void,
  ): Promise<PluginListenerHandle>;
  removeAllListeners(): Promise<void>;
}

export const NativeSpeechRecognition = registerPlugin<SpeechRecognitionPlugin>('SpeechRecognition');
